import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { styled } from "@mui/material/styles";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Button, IconButton, Stack } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DevelopmentNeedStatusIcon from "./DevelopmentNeedStatusIcon";
import PageviewOutlinedIcon from "@mui/icons-material/PageviewOutlined";

import RenderDate from "../../../Common/Date/RenderDate";
import DevelopmentNeedDialog from "./DevelopmentNeedDialog";
import DevelopmentNeedFormDialog from "./DevelopmentNeedFormDialog";
import PDPFormDialog from "../PDP/PDPFormDialog";
import StatusDialog from "./StatusDialog";

import { addDevelopmentNeed, deleteDevelopmentNeed, updateDevelopmentNeed, updatePDP, getAppraisalDetails } from "../../../../actions/rmsActions";


const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        padding: 12,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 18,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const LinkBuilder = ({ text, id, enabled = false }) => {
    const location = useLocation();
    const refererPath = encodeURIComponent(location.pathname);
    const label = text?.length > 0 ? text : "No title";
    let display = label;
    if (enabled && id) {
        display = (
            <Link to={`/portfolio/pdps/${Number(id)}/?rp=${refererPath}`}>
                {label}
            </Link>
        );
    }
    return display;
};


function DevelopmentNeedTable({items, appraisalId, pdpId, isAppraiser, appraiseeUUID, canAdd, canComplete = false, embedded = false, appCode}) {
    const [openDialog, setOpenDialog] = useState(null);
    const [selected, setSelected] = useState(null);

    const dispatch = useDispatch();
    const handleAdd = (params) => {
        let appraiseeParams = null;
        if (!!isAppraiser && !!appraiseeUUID) {
            appraiseeParams = { appraiseeUUID, model: "review" };
            params = { ...params, ...appraiseeParams, status: "agreed" };
        }
        dispatch(addDevelopmentNeed(
            appCode,
            !pdpId ? params : { ...params, pdpId },
            appraisalId,
            () => dispatch(getAppraisalDetails(appraisalId, appCode, appraiseeParams)))
        );
    };
    const handleEdit = (id, params) => {
        if (!!isAppraiser && !!appraiseeUUID) {
            params = { ...params, appraiseeUUID, model: "review" };
        }
        dispatch(updateDevelopmentNeed(appCode, id, params, appraisalId));
    };
    const handleDelete = (id) => dispatch(deleteDevelopmentNeed(appCode, id, appraisalId));

    return (
        <>
            <TableContainer>
                <Table sx={{ minWidth: 500 }} aria-label="customized table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Development needs</StyledTableCell>
                            <StyledTableCell align="center">
                                Review
                            </StyledTableCell>
                            <StyledTableCell align="center">
                                Completion
                            </StyledTableCell>
                            <StyledTableCell align="center">
                                End
                            </StyledTableCell>
                            <StyledTableCell align="center">
                                Status
                            </StyledTableCell>
                            <StyledTableCell>&nbsp;</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(items) &&
                            items.sort((a, b) => a.dateCompletion - b.dateCompletion)
                                .map((row) => (
                                    <StyledTableRow key={row?.id}>
                                        <StyledTableCell>
                                            <LinkBuilder
                                                text={row?.title}
                                                id={row?.pdpId}
                                                enabled={embedded}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <RenderDate
                                                date={row?.dateReview}
                                                colourise={!row?.status === "completed"}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <RenderDate
                                                date={row?.dateCompletion}
                                                colourise={!row?.status === "completed"}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <RenderDate
                                                date={row?.dateEnd}
                                                colourise={false}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Stack
                                                direction="row"
                                                justifyContent="flex-end"
                                                spacing={0}
                                            >
                                                {row?.options?.length > 0 ? (
                                                    <IconButton
                                                        aria-label="status"
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setOpenDialog("status");
                                                        }}
                                                    >
                                                        <DevelopmentNeedStatusIcon status={row?.status} />
                                                    </IconButton>
                                                ) : (
                                                    <DevelopmentNeedStatusIcon status={row?.status} />
                                                )}
                                            </Stack>
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                            <Stack
                                                direction="row"
                                                justifyContent="flex-end"
                                                spacing={0}
                                            >
                                                <IconButton
                                                    aria-label="view"
                                                    onClick={() => {
                                                        setSelected(row);
                                                        setOpenDialog("view");
                                                    }}
                                                >
                                                    <PageviewOutlinedIcon />
                                                </IconButton>
                                                <IconButton
                                                    aria-label="edit"
                                                    disabled={!row.canUpdate}
                                                    onClick={() => {
                                                        setSelected(row);
                                                        setOpenDialog("edit");
                                                    }}
                                                >
                                                    <EditOutlinedIcon />
                                                </IconButton>
                                                <IconButton
                                                    aria-label="delete"
                                                    disabled={!row.canDelete}
                                                    onClick={() => {
                                                        setSelected(row);
                                                        setOpenDialog("delete");
                                                    }}
                                                >
                                                    <DeleteOutlinedIcon />
                                                </IconButton>
                                            </Stack>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                        <StyledTableRow key="totals">
                            <StyledTableCell
                                component="th"
                                scope="row"
                                sx={{ backgroundColor: "#FFFFFF" }}
                                colSpan={6}
                            ></StyledTableCell>
                        </StyledTableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={2}
            >
                {canAdd && (
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        color="secondary"
                        disableElevation={true}
                        aria-label="add development need"
                        onClick={() => setOpenDialog("add")}
                    >
                        Add
                    </Button>
                )}
                {(canComplete && !!pdpId) && (
                    <Button
                        endIcon={<CheckOutlinedIcon style={{ color: "#FFF" }}/>}
                        variant="contained"
                        color="secondary"
                        disableElevation
                        aria-label="complete pdp"
                        onClick={() => setOpenDialog("complete")}
                    >
                        Complete PDP
                    </Button>
                )}
            </Stack>

            {(openDialog === "add" || (openDialog === "edit" && !!selected)) && (
                <DevelopmentNeedFormDialog
                    open={true}
                    formTitle={`${openDialog === "add" ? "Add" : "Edit"} development need`}
                    data={openDialog === "edit" && selected}
                    onSave={openDialog === "add" ? handleAdd : (params) => handleEdit(selected.id, params)}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    submitLabel={`${openDialog === "add" ? "Add" : "Save"}`}
                    scroll="paper"
                />
            )}

            {openDialog === "view" && !!selected && (
                <DevelopmentNeedDialog
                    open={true}
                    data={selected}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                />
            )}

            {openDialog === "status" && !!selected && (
                <StatusDialog
                    open={true}
                    data={selected}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    onSave={(params) => handleEdit(selected.id, params)}
                />
            )}

            {openDialog === "delete" && !!selected && (
                <ConfirmDialog
                    open={true}
                    title="Delete development need"
                    content={`The following development need will be deleted: ${selected.title}`}
                    confirmButtonLabel="Delete"
                    onConfirm={() => handleDelete(selected.id)}
                    onCancel={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                />
            )}

            {openDialog === "complete" && (
                <PDPFormDialog
                    open
                    canComplete
                    formTitle="Complete PDP"
                    submitLabel="Complete"
                    onSave={() => dispatch(updatePDP(appCode, pdpId, {status: 'completed'}))}
                    onClose={() => setOpenDialog(null)}
                />
            )}
        </>
    );
}
export default DevelopmentNeedTable;
